import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type ListUsersResponse =
  paths["/users"]["get"]["responses"]["200"]["content"]["application/json"];

export type UserListItem = ListUsersResponse["items"][number];

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: UserListItem[]; totalCount: number }
  | { kind: "error"; message: string };

export type StatusFilter = "" | "active" | "inactive";

export interface UseUserListResult {
  state: ScreenState;
  fetching: boolean;
  search: string;
  roleId: string;
  status: StatusFilter;
  offset: number;
  limit: number;
  setSearch: (value: string) => void;
  setRoleId: (value: string) => void;
  setStatus: (value: StatusFilter) => void;
  setOffset: (value: number) => void;
  setLimit: (value: number) => void;
  refetch: () => void;
}

const DEFAULT_LIMIT = 25;

export function useUserList(): UseUserListResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [search, setSearchValue] = useState("");
  const [roleId, setRoleIdValue] = useState("");
  const [status, setStatusValue] = useState<StatusFilter>("");
  const [offset, setOffsetValue] = useState(0);
  const [limit, setLimitValue] = useState(DEFAULT_LIMIT);
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchUsers = useCallback(async () => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (!initialLoadDone.current) {
      setState({ kind: "loading" });
    }
    setFetching(true);

    const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (search.trim().length > 0) {
      query.set("search", search.trim());
    }
    if (roleId.length > 0) {
      query.set("roleId", roleId);
    }
    if (status.length > 0) {
      query.set("status", status);
    }

    try {
      const response = await fetchWithRefresh(`/api/users?${query.toString()}`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = (await response.json()) as ListUsersResponse;
      initialLoadDone.current = true;
      if (data.items.length === 0) {
        setState({ kind: "empty" });
      } else {
        setState({ kind: "data", items: data.items, totalCount: data.totalCount });
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      logger.error({ err: message }, "Failed to fetch users");
      setState({ kind: "error", message });
    } finally {
      if (!controller.signal.aborted) {
        setFetching(false);
      }
    }
  }, [search, roleId, status, offset, limit]);

  useEffect(() => {
    void fetchUsers();
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [fetchUsers]);

  const setSearch = useCallback((value: string) => {
    setOffsetValue(0);
    setSearchValue(value);
  }, []);

  const setRoleId = useCallback((value: string) => {
    setOffsetValue(0);
    setRoleIdValue(value);
  }, []);

  const setStatus = useCallback((value: StatusFilter) => {
    setOffsetValue(0);
    setStatusValue(value);
  }, []);

  const setOffset = useCallback((value: number) => {
    setOffsetValue(value);
  }, []);

  const setLimit = useCallback((value: number) => {
    setOffsetValue(0);
    setLimitValue(value);
  }, []);

  const refetch = useCallback(() => {
    void fetchUsers();
  }, [fetchUsers]);

  return { state, fetching, search, roleId, status, offset, limit, setSearch, setRoleId, setStatus, setOffset, setLimit, refetch };
}
